import { useState, useMemo } from 'react';
import { NumPad } from './NumPad';

interface PhoneDialogProps {
  onSubmit: (phone: string) => void;
  onSkip: () => void;
}

function formatPhone(raw: string): string {
  const digits = raw.slice(0, 10);
  const groups: string[] = [];
  for (let i = 0; i < digits.length; i += 2) {
    groups.push(digits.slice(i, i + 2));
  }
  return groups.join(' ');
}

export function PhoneDialog({ onSubmit, onSkip }: PhoneDialogProps) {
  const [phone, setPhone] = useState('');

  const isValid = phone.length === 10;

  const displayValue = useMemo(() => formatPhone(phone), [phone]);

  function handleSubmit() {
    if (isValid) {
      onSubmit(phone);
    }
  }

  return (
    <div className="overlay">
      <div className="bg-surface-card rounded-3xl p-8 w-[480px] max-w-[90vw] flex flex-col items-center gap-5 animate-fade-in">
        {/* Phone icon */}
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="text-accent">
          <path
            d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.2 2.2z"
            fill="currentColor"
          />
        </svg>

        <h2 className="text-2xl font-bold text-white">Votre numero de telephone</h2>
        <p className="text-white/50 text-sm -mt-2">Pour votre carte de fidelite</p>

        {/* Formatted display */}
        <div className="bg-surface rounded-xl px-6 py-4 min-h-[56px] w-full max-w-[340px] flex items-center justify-center">
          {phone.length > 0 ? (
            <span className="text-2xl font-bold text-white tracking-widest">
              {displayValue}
            </span>
          ) : (
            <span className="text-xl text-white/30">06 12 34 56 78</span>
          )}
        </div>

        {/* NumPad without its own display */}
        <div className="w-full max-w-[340px]">
          <div className="grid grid-cols-3 gap-3">
            {(['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0'] as const).map(
              (key) => {
                const isClear = key === 'C';
                let className =
                  'bg-surface-light rounded-xl min-h-[64px] text-2xl font-semibold transition-all duration-150 active:bg-surface-card active:scale-95 flex items-center justify-center';
                if (isClear) {
                  className += ' text-danger';
                } else {
                  className += ' text-white';
                }
                return (
                  <button
                    key={key}
                    type="button"
                    className={className}
                    onClick={() => {
                      if (key === 'C') {
                        setPhone('');
                      } else if (phone.length < 10) {
                        setPhone(phone + key);
                      }
                    }}
                  >
                    {key}
                  </button>
                );
              },
            )}
            {/* Delete last digit */}
            <button
              type="button"
              className="bg-surface-light rounded-xl min-h-[64px] text-2xl font-semibold transition-all duration-150 active:bg-surface-card active:scale-95 flex items-center justify-center text-white/50"
              onClick={() => setPhone(phone.slice(0, -1))}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-white/50">
                <path
                  d="M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.12c.36.53.9.88 1.59.88h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-3 12.59L17.59 17 14 13.41 10.41 17 9 15.59 12.59 12 9 8.41 10.41 7 14 10.59 17.59 7 19 8.41 15.41 12 19 15.59z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Submit */}
        <button
          type="button"
          className="btn-primary w-full max-w-[340px] text-center text-lg disabled:opacity-40 disabled:cursor-not-allowed"
          disabled={!isValid}
          onClick={handleSubmit}
        >
          Valider
        </button>

        {/* Skip */}
        <button
          type="button"
          className="text-white/50 text-sm underline underline-offset-2"
          onClick={onSkip}
        >
          Passer
        </button>
      </div>
    </div>
  );
}
